// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Warble — colour system + WCAG contrast helper.
//
// The play surface is a dark indigo night with one warm mote and a luminous cyan
// ribbon, drawn on canvas. The colours that carry MEANING and must clear the 3:1
// graphic floor (principle #22) against the surface they sit on (the stage, the
// darker edge vignette, and the DOM panels): the MOTE (its three gradient stops),
// the RIBBON, the WALL (Thread), the GAP a wall leaves, the SPARKS, and the text.
// Colour is never the only cue — the mote is a filled disc with a trail, the ribbon
// a line, a wall a barred rectangle with a notch — and confusable same-context pairs
// (ribbon↔gap, gap↔wall) are also separated in LIGHTNESS. Every meaningful colour is
// pinned by contrast.test.ts, and the real painted pixels are probed in Step 11.

export const PALETTE = {
  // stage + panels
  stage: '#0b1020', // the canvas backdrop
  vignette: '#070a15', // the darker ring painted at the stage edge
  panel: '#131a30',
  panelDeep: '#0e1526',

  // text
  text: '#eef3f8',
  textMuted: '#a7b7cc',
  ink: '#05070d', // dark glyph on a bright fill (the combo number on the mote)

  // the mote — a warm amber gradient (core → mid → edge)
  moteCore: '#ffe6a6',
  moteMid: '#f6b73e',
  moteEdge: '#c98620',
  sheen: '#fff3d6',

  // roles (each also carries a shape/behaviour cue)
  ribbon: '#3fd0d8', // the cyan ribbon of light you fly along (a line)
  wall: '#ff7a5c', // Thread walls (a coral bar) — miss the gap and the run ends
  gap: '#7be0a0', // the safe passage a wall leaves (green, distinct by hue AND lightness)
  spark: '#ffd98a', // on-ribbon sparks / combo particles

  // opponents in a race
  rival: '#9db5d4',
} as const;

/* ── WCAG contrast ────────────────────────────────────────────────────────── */

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** Relative luminance of a #rrggbb colour (WCAG 2.1). */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) throw new Error(`not a hex colour: ${hex}`);
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => channel(parseInt(h, 16)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two #rrggbb colours (1..21, symmetric). */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
